/**
 * Generic builder for the `ml_lens` envelope that lets the ML Lens transformer
 * split a single GASv3 event into one metric per logical dimension (e.g. one
 * metric per product/connector).
 *
 * See: https://hello.atlassian.net/wiki/spaces/~93171358/pages/6820686403/
 *   GASv3+Events+Transformation+for+ML+Lens+Metrics+Schema
 *
 * This module is intentionally domain-agnostic: callers provide the namespace,
 * the metric-set base tags and the list of metrics. Surface-specific helpers
 * (e.g. Quick Find post-query, Full Page Search results) live alongside this
 * file and delegate to {@link buildMlLensEnvelope}.
 */

export type MlLensMetric = {
	key: string;
	type: 'number';
	value: number;
	tags: Record<string, string>;
};

export type MlLensMetricSet = {
	namespace: string;
	tags: Record<string, string>;
	metrics: MlLensMetric[];
};

export type MlLensEnvelope = {
	version: '1';
	metric_sets: MlLensMetricSet[];
};

export type BuildMlLensEnvelopeArgs = {
	/** Metric-set namespace, e.g. `rovo.quick_find` or `rovo.full_search_page`. */
	namespace: string;
	/** Base tags applied to the metric set, e.g. `{ page, task }`. */
	baseTags: Record<string, string>;
	/**
	 * The metrics for this metric set. Pass `[]` to emit an envelope with no
	 * metrics (e.g. a "succeeded with zero results" path) so downstream
	 * consumers can distinguish it from "ml_lens was not attached at all".
	 */
	metrics: MlLensMetric[];
	/**
	 * Current user id (Atlassian account id). When provided (non-empty), it is
	 * added to the metric-set `tags` as `user_id`. Omitted entirely when
	 * `null`/`undefined`/empty so the tag is never emitted as an empty string.
	 */
	userId?: string | null;
};

/**
 * Build a versioned `ml_lens` envelope with a single metric set.
 */
export const buildMlLensEnvelope = ({
	namespace,
	baseTags,
	metrics,
	userId,
}: BuildMlLensEnvelopeArgs): MlLensEnvelope => ({
	version: '1',
	metric_sets: [
		{
			namespace,
			tags: { ...baseTags, ...(userId ? { user_id: userId } : {}) },
			metrics,
		},
	],
});
